import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'motion/react';
import CTAButton from '../../../shared/components/buttons/CTAButton';

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 3l1.82 4.74L18.5 9l-4.05 3.2L15.1 17 12 14.8 8.9 17l0.65-4.8L5.5 9l4.68-1.26L12 3z" />
  </svg>
);

const PricingSection = ({
  billingPeriod = 'monthly',
  onToggleBillingPeriod = () => {},
  features = [],
}) => {
  const isYearly = billingPeriod === 'yearly';
  const priceDisplay = isYearly ? '£8.25' : '£15';
  const cadence = isYearly ? '£99 billed yearly - Save 34%' : 'Billed monthly';

  return (
    <section className="relative overflow-hidden py-24" style={{ background: 'var(--canvas)' }}>
      <motion.div
        className="pointer-events-none absolute right-[8%] top-[15%] h-[450px] w-[450px] rounded-full opacity-[0.018] blur-[100px]"
        style={{ background: 'radial-gradient(circle, var(--accent-primary) 0%, transparent 70%)' }}
        animate={{ x: [0, -35, 0], y: [0, 25, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative mx-auto w-full max-w-6xl px-6">
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-decorative text-xs uppercase tracking-[0.35em]" style={{ color: 'var(--terracotta)', letterSpacing: '0.35em' }}>
            Pricing
          </p>
          <p className="font-display text-3xl text-[var(--color-text-primary)] sm:text-4xl" style={{ letterSpacing: '-0.02em' }}>
            Simple, Transparent Pricing
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-base text-[var(--color-text-secondary)]" style={{ lineHeight: 1.7 }}>
            One plan. All the tools you need to grow your food business.
          </p>
        </motion.div>

        <motion.div
          className="mb-12 flex flex-wrap items-center justify-center gap-3"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
        >
          <span
            className={`text-sm font-medium ${
              !isYearly ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'
            }`}
          >
            Monthly
          </span>
          <button
            type="button"
            onClick={onToggleBillingPeriod}
            className="relative h-8 w-16 rounded-full border border-[var(--shell-border)] bg-[var(--surface-muted)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
            style={{
              background: isYearly
                ? 'linear-gradient(120deg, var(--terracotta), rgba(200,106,74,0.7))'
                : 'var(--surface-muted)',
            }}
            aria-label="Toggle billing period"
          >
            <span
              className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-[0_10px_30px_rgba(22,23,25,0.15)] transition-all duration-300 ${
                isYearly ? 'right-1 left-auto' : 'left-1 right-auto'
              }`}
            />
          </button>
          <span
            className={`text-sm font-medium ${
              isYearly ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'
            }`}
          >
            Yearly <span className="text-xs font-semibold text-[var(--terracotta)]">(Save 34%)</span>
          </span>
        </motion.div>

        <motion.div
          className="mx-auto max-w-3xl"
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="relative overflow-hidden rounded-[28px] border border-[var(--shell-border)] bg-[var(--surface)] p-10 shadow-[var(--shadow-soft)]"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.12] blur-3xl"
              style={{ background: 'radial-gradient(circle at top, var(--accent-primary) 0%, var(--accent-soft) 50%, transparent 70%)' }}
            />

            <div className="relative">
              <div className="mb-6">
                <span
                  className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]"
                  style={{
                    background: 'linear-gradient(120deg, #DB8F71, var(--terracotta))',
                    color: '#fff',
                    boxShadow: '0 18px 40px -24px rgba(200,106,74,0.65)',
                  }}
                >
                  <SparkleIcon />
                  Everything You Need
                </span>
                <h3 className="mt-5 font-display text-3xl text-[var(--color-text-primary)]">Kinn Starter</h3>
                <p className="text-sm text-[var(--color-text-secondary)]">Perfect for growing your food business</p>
              </div>

              <div className="mb-8">
                <div className="flex items-baseline gap-3">
                  <span
                    className="text-5xl font-bold tracking-tight"
                    style={{ color: 'var(--color-text-primary)', letterSpacing: '-0.03em' }}
                  >
                    {priceDisplay}
                  </span>
                  <span className="text-lg text-[var(--color-text-secondary)]">/month</span>
                </div>
                <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{cadence}</p>
              </div>

              <div className="mb-10 space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <span
                      className="mt-1 flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold"
                      style={{ background: 'var(--terracotta-pale)', color: 'var(--terracotta)' }}
                    >
                      ✓
                    </span>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-[var(--color-text-primary)]">{feature.title}</p>
                        {feature.badge && (
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                            style={{ background: 'var(--accent-primary)' }}
                          >
                            {feature.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--color-text-secondary)]">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <CTAButton
                as={NavLink}
                to="/seller/get-started"
                size="lg"
                fullWidth
                className="justify-center"
              >
                Launch Your Kinn Store
              </CTAButton>
              <p className="mt-4 text-center text-xs text-[var(--color-text-secondary)]">
                💡 30-day free trial • Cancel anytime • No hidden fees
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
